import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UserActivityCategory, UserActivityTypes } from '@pascal/iframe-service';
import { observer } from 'mobx-react-lite';

import { convertAmount, formatFreeDropRemainingTime, isMobile, resizeContent } from '../../../../../helpers';
import { FreeDropDrop } from '../../../../../models';
import { useStore } from '../../../../../hooks';

import ProgressBarBubbleYellow from '../../../../../assets/images/free-drop/yellow-progress-gift.gif';
import ProgressBarBubbleOrange from '../../../../../assets/images/free-drop/orange-progress-gift.gif';
import ProgressBarBubbleRed from '../../../../../assets/images/free-drop/red-progress-gift.gif';


interface FreeDropProgressBarProps {
  nextDropDate: Date | null;
  nextDrop: FreeDropDrop | null;
}

export const FreeDropProgressBar: React.FC<FreeDropProgressBarProps> = observer(({ nextDrop, nextDropDate }) => {

  const { freeDropStore, gameStore, userActivityStore } = useStore();
  const { currencyCode, rate } = gameStore;
  const { levels, collectedAmount, showProgressBar, freeDropInfo } = freeDropStore;

  const progressWrapper = useRef<HTMLDivElement | null>(null);

  const [ nextDropRemainingTime, setNextDropRemainingTime ] = useState('');
  const [ highlighting, setHighlighting ] = useState(false);

  const playerProgress = useMemo(() => {
    const tiersOrderedByGoal = [...nextDrop?.tiers || []].sort((a, b) => b.goal < a.goal ? 1 : -1);

    const [ firstGoal, secondGoal, thirdGoal ] = tiersOrderedByGoal.map(({ freeBets }) => {
      const userCurrencyfreebet = freeBets.find(({ currency }) => currency === currencyCode);
      
      if (!userCurrencyfreebet) {
        const campaignCurrencyfreebet = freeBets.find(({ currency }) => currency === freeDropInfo?.currency);
        return campaignCurrencyfreebet ? convertAmount(campaignCurrencyfreebet.goalCoinAmount, rate, 0) : 0;
      }
      return userCurrencyfreebet?.goal || 0;
    });
    
    const sectionHeightByPercent = 100 / (tiersOrderedByGoal.length || 1);
    if (collectedAmount <= firstGoal) {
      // Section 1 [0 to firstGoal]
      const range = firstGoal;
      // Formula: (amount / range) * sectionHeight
      return (collectedAmount / range) * sectionHeightByPercent;
    } else if (collectedAmount <= secondGoal) {
      // Section 2 (firstGoal to secondGoal]
      const range = secondGoal - firstGoal;
      // Formula: Base + ((amount - startOfSection) / range) * sectionHeight
      return sectionHeightByPercent + ((collectedAmount - firstGoal) / range) * sectionHeightByPercent;
    } else if (collectedAmount <= thirdGoal) {
      // Section 3 (secondGoal to thirdGoal]
      const range = thirdGoal - secondGoal;
      // Formula: Base + ((amount - startOfSection) / range) * sectionHeight
      return (sectionHeightByPercent * 2) + ((collectedAmount - secondGoal) / range) * sectionHeightByPercent;
    } else {
      return 100;
    }
  }, [nextDrop, collectedAmount, rate, currencyCode, freeDropInfo]);

  useEffect(() => {
    const progressWrapperElement = progressWrapper.current;
    if (progressWrapperElement && !isMobile()) {
      resizeContent({ target: progressWrapperElement });
      window.addEventListener('resize', () => resizeContent({ target: progressWrapperElement }));
    }
  }, []);

  useEffect(() => {
    setHighlighting(true);
    setTimeout(() => {
      setHighlighting(false);
    }, 1200);
  }, [playerProgress]);

  useEffect(() => {
    let interval: NodeJS.Timer;
    if (nextDropDate) {
      interval = setInterval(() => {
        setNextDropRemainingTime(formatFreeDropRemainingTime(nextDropDate));
      }, 1000);
    }
    return () => {
      clearInterval(interval);
    };
  }, [nextDropDate]);

  const openDetailsPopup = useCallback(() => {
    userActivityStore.sendTrackUserActivity({
      category: UserActivityCategory.PromoTools,
      type: UserActivityTypes.FreeDropsWidget
    });
    freeDropStore.setShowDetailsPopup(true);
  }, []);

  const progressBarBubbleImg = useMemo(() => {
    if (levels.length === 3) {
      if (playerProgress <= 33.33) return ProgressBarBubbleYellow;
      if (playerProgress <= 66.66) return ProgressBarBubbleOrange;
      return ProgressBarBubbleRed;
    }
    if (levels.length === 2) {
      if (playerProgress <= 50) return ProgressBarBubbleYellow;
      return ProgressBarBubbleOrange;
    }
    return ProgressBarBubbleYellow;
  }, [levels, playerProgress]);

  return (
    <div className={`progress-bar-wrapper ${showProgressBar ? 'show' : ''}`} ref={progressWrapper}>
      <div className="info-icon-for-popup" onClick={openDetailsPopup}>
        <i className="icon-info info-icon-popup" />
      </div>
      <div className="progress-bar-holder" onClick={openDetailsPopup}>
        <div className={`progress-bar-inner ${ playerProgress === 100 ? 'active' : '' } ${highlighting ? 'highlight' : '' }`}>
          <div className="progress-bar-block">
            <div className="progress-bar-line-wrp">
              {
                levels.map((number, index) => (
                  index ? (
                    <span key={number} className="progress-bar-line" />
                  ) : null
                ))
              }
            </div>
            <div
              className="progress-bar-block-holder"
              style={{ height: `${playerProgress}%` }}
            >
              <div className={`progress-bar-block-inner type${levels.length}`} />
              <div className="progress-bar-bubble">
                <img
                  alt="bubble-img"
                  src={progressBarBubbleImg}
                  className="progress-bar-bubble-img"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <span className="progress-bar-timer">{ nextDropRemainingTime }</span>
    </div>
  );
});
